import { IconListDetails, IconRobot } from '@tabler/icons-react';
import type { NavItemType } from 'types';
import type { WorkflowStatusId } from 'types/workflow/workflow-status';

/** טאב סרגל שיחות: שלי | צוות (audience=team) | כל השיחות */
export type ChatSidebarScopeTab = 'mine' | 'team' | 'all';

/** תצוגת «ממתינות לשיוך» אינה מוצגת בתוך שלי/צוות — רק בתיקיית «לא משויכות» וב«הכל» */
export const SCOPES_WITHOUT_INLINE_UNASSIGNED: ChatSidebarScopeTab[] = ['mine', 'team'];

export type UnifiedStatusKind =
  | 'awaitingReply'
  | 'unassigned'
  | 'awaitingCustomer'
  | 'firstReply'
  | 'reminder'
  | 'completed';

/** שורה ראשונה + סטטוסי workflow */
export type UnifiedListKind = 'allConversations' | UnifiedStatusKind;

export const UNIFIED_LIST_ORDER: UnifiedListKind[] = [
  'allConversations',
  'awaitingReply',
  'unassigned',
  'awaitingCustomer',
  'firstReply',
  'reminder',
  'completed',
];

/** סדר תצוגה קבוע: להמשך מענה → לשיוך לנציג → לאיש קשר → למענה ראשוני → בהקפאה → הסתיימו */
export const UNIFIED_STATUS_ORDER: UnifiedStatusKind[] = [
  'awaitingReply',
  'unassigned',
  'awaitingCustomer',
  'firstReply',
  'reminder',
  'completed',
];

const ALL_CONV_TITLE = 'sidebar.nav.views.allConversationsRow';
const ALL_CONV_TOOLTIP = 'sidebar.nav.views.tooltip.allConversationsRow';

const TITLE: Record<UnifiedStatusKind, string> = {
  awaitingReply: 'sidebar.nav.views.awaitingReply',
  unassigned: 'sidebar.nav.views.unassignedPendingAssign',
  awaitingCustomer: 'sidebar.nav.views.awaitingCustomerShort',
  firstReply: 'sidebar.nav.views.firstReply',
  reminder: 'sidebar.nav.views.reminderByDate',
  completed: 'sidebar.nav.views.completedArchive',
};

const TOOLTIP: Record<UnifiedStatusKind, string> = {
  awaitingReply: 'sidebar.nav.views.tooltip.awaitingReply',
  unassigned: 'sidebar.nav.views.tooltip.unassignedPendingAssign',
  awaitingCustomer: 'sidebar.nav.views.tooltip.awaitingCustomer',
  firstReply: 'sidebar.nav.views.tooltip.firstReply',
  reminder: 'sidebar.nav.views.tooltip.reminderByDate',
  completed: 'sidebar.nav.views.tooltip.completedArchive',
};

/** מיפוי תצוגה → סטטוס Flow (1–6) — אייקונים מ־workflow-status-meta */
export const UNIFIED_KIND_TO_WORKFLOW_STATUS: Record<UnifiedStatusKind, WorkflowStatusId> = {
  unassigned: 1,
  firstReply: 2,
  awaitingCustomer: 3,
  awaitingReply: 4,
  reminder: 5,
  completed: 6,
};

const TONE: Record<UnifiedStatusKind, 'urgent' | 'newWork' | 'muted'> = {
  awaitingReply: 'urgent',
  unassigned: 'urgent',
  awaitingCustomer: 'muted',
  firstReply: 'newWork',
  reminder: 'muted',
  completed: 'muted',
};

export function buildAllConversationsUrl(scope: ChatSidebarScopeTab): string {
  if (scope === 'mine') return '/chats/my';
  if (scope === 'team') return '/chats/not-my';
  return '/chats';
}

function buildUrl(scope: ChatSidebarScopeTab, kind: UnifiedStatusKind): string {
  switch (kind) {
    case 'unassigned':
      return '/chats/unassigned';
    case 'completed':
      return '/chats/completed';
    case 'reminder':
      if (scope === 'mine') return '/chats/my/snoozed';
      if (scope === 'team') return '/chats/not-my?wf=5';
      return '/chats?wf=5';
    case 'awaitingReply':
      if (scope === 'mine') return '/chats/my?wf=4';
      if (scope === 'team') return '/chats/not-my?wf=4';
      return '/chats?wf=4';
    case 'awaitingCustomer':
      if (scope === 'mine') return '/chats/my?wf=3';
      if (scope === 'team') return '/chats/not-my?wf=3';
      return '/chats?wf=3';
    case 'firstReply':
      if (scope === 'mine') return '/chats/my?wf=2';
      if (scope === 'team') return '/chats/not-my?wf=2';
      return '/chats?wf=2';
    default:
      return '/chats';
  }
}

export function buildAllConversationsNavItem(scope: ChatSidebarScopeTab): NavItemType {
  const id = `unified-${scope}-allConversations`;
  return {
    id,
    title: ALL_CONV_TITLE,
    icon: IconListDetails,
    type: 'item',
    url: buildAllConversationsUrl(scope),
    breadcrumbs: false,
    counterTone: 'muted',
    viewTooltipId: ALL_CONV_TOOLTIP,
  };
}

/** פריט תפריט יציב לזיהוי ול־NavItem */
export function buildUnifiedNavItem(scope: ChatSidebarScopeTab, kind: UnifiedStatusKind): NavItemType {
  const id = `unified-${scope}-${kind}`;
  const base: NavItemType = {
    id,
    title: TITLE[kind],
    workflowStatusId: UNIFIED_KIND_TO_WORKFLOW_STATUS[kind],
    type: 'item',
    url: buildUrl(scope, kind),
    breadcrumbs: false,
    counterTone: TONE[kind],
    viewTooltipId: TOOLTIP[kind],
  };
  /** מתוך «שלי» / «צוות» הקישור לאותה שורה הוא קיצור דרך לתור השיוך הכללי — לא תור «שלי» */
  if (kind === 'unassigned' && (scope === 'mine' || scope === 'team')) {
    return {
      ...base,
      globalQueueShortcut: true,
      viewTooltipId:
        scope === 'mine'
          ? 'sidebar.nav.views.tooltip.unassignedPendingAssignShortcutMine'
          : 'sidebar.nav.views.tooltip.unassignedPendingAssignShortcutTeam',
    };
  }
  return base;
}

export function buildUnifiedListItem(scope: ChatSidebarScopeTab, kind: UnifiedListKind): NavItemType {
  if (kind === 'allConversations') return buildAllConversationsNavItem(scope);
  return buildUnifiedNavItem(scope, kind);
}

/** מזהה תצוגת «בוטים» במסך ניהול התצוגות (SYSTEM_VIEWS) — שורה גלובלית, מנוהלת תחת «כל השיחות» */
export const BOTS_SYSTEM_VIEW_ID = 'bots-chats';

/** שורת «בוטים» — שיחות שמוחזקות כרגע אצל בוט; מוחרגות מכל שאר התצוגות והמונים */
export function buildBotsNavItem(): NavItemType {
  return {
    id: 'unified-bots',
    title: 'sidebar.nav.views.botsRow',
    icon: IconRobot,
    type: 'item',
    url: '/chats/bots',
    breadcrumbs: false,
    counterTone: 'muted',
    viewTooltipId: 'sidebar.nav.views.tooltip.bots',
  };
}

/** נראות שורת «בוטים» — נשלטת במסך ניהול התצוגות; ברירת מחדל גלויה לכולם */
export function isBotsRowVisible(userViewsConfig: Record<string, boolean> | undefined): boolean {
  if (!userViewsConfig) return true;
  const folderAware = userViewsConfig[`all:${BOTS_SYSTEM_VIEW_ID}`];
  const effective = folderAware !== undefined ? folderAware : userViewsConfig[BOTS_SYSTEM_VIEW_ID];
  return effective !== false;
}

/** כל צירופי scope×kind — ל־menu-items (הדגשת נתיב / יתומה) */
export function getAllUnifiedViewMenuItems(): NavItemType[] {
  const scopes: ChatSidebarScopeTab[] = ['mine', 'team', 'all'];
  const out: NavItemType[] = [];
  for (const scope of scopes) {
    for (const kind of UNIFIED_LIST_ORDER) {
      out.push(buildUnifiedListItem(scope, kind));
    }
  }
  out.push(buildBotsNavItem());
  return out;
}

/** מיפוי kind → מזהה תצוגת מערכת במסך ניהול התצוגות (SYSTEM_VIEWS ב-views-visibility-data) */
export const UNIFIED_KIND_TO_SYSTEM_VIEW_ID: Partial<Record<UnifiedListKind, string>> = {
  allConversations: 'all-conversations',
  unassigned: 'unassigned-chats',
  firstReply: 'newly-assigned',
  awaitingReply: 'awaiting-me',
  awaitingCustomer: 'waiting-customer',
  reminder: 'my-chats-snoozed',
  completed: 'chats-completed',
};

/**
 * האם שורת סטטוס גלויה בתיקייה נתונה — משלב שני מנגנונים:
 * נראות פר-תיקייה (perScopeStatusVisibility) + ViewsConfig מנוהל-אדמין,
 * שבו המפתח החדש כולל ממד תיקייה ("mine:awaiting-me") עם fallback למפתח הישן חסר-התיקייה.
 */
export function isUnifiedRowVisibleInFolder(
  folderScope: ChatSidebarScopeTab,
  kind: UnifiedListKind,
  statusRows: Record<string, boolean> | undefined,
  userViewsConfig: Record<string, boolean> | undefined,
): boolean {
  if (statusRows && statusRows[kind] === false) return false;
  const viewId = UNIFIED_KIND_TO_SYSTEM_VIEW_ID[kind];
  if (viewId && userViewsConfig) {
    const folderAware = userViewsConfig[`${folderScope}:${viewId}`];
    const effective = folderAware !== undefined ? folderAware : userViewsConfig[viewId];
    if (effective === false) return false;
  }
  return true;
}

/**
 * האם לתיקייה מובנית יש לפחות שורה מובנית אחת גלויה (שורת «כל השיחות» + סטטוסים).
 * `skipUnassignedRow` — לתפריט המכווץ, שבו «לשיוך לנציג» כבר מוצגת כשורה עצמאית
 * ולכן מסוננת מתוך «כל השיחות» כדי לא להופיע פעמיים.
 */
export function folderHasVisibleBuiltinRows(
  folderScope: ChatSidebarScopeTab,
  statusRows: Record<string, boolean> | undefined,
  userViewsConfig: Record<string, boolean> | undefined,
  skipUnassignedRow = false,
): boolean {
  for (const kind of UNIFIED_LIST_ORDER) {
    if (kind === 'unassigned' && (skipUnassignedRow || SCOPES_WITHOUT_INLINE_UNASSIGNED.includes(folderScope))) {
      continue;
    }
    if (isUnifiedRowVisibleInFolder(folderScope, kind, statusRows, userViewsConfig)) return true;
  }
  return false;
}

export function scopeTabFromIndex(index: number): ChatSidebarScopeTab {
  if (index === 1) return 'team';
  if (index === 2) return 'all';
  return 'mine';
}

export function indexFromScopeTab(tab: ChatSidebarScopeTab): number {
  if (tab === 'team') return 1;
  if (tab === 'all') return 2;
  return 0;
}
